"use client";

import { Button, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";

export type TournamentTier = "basic" | "competitive" | "premium";

// Medieval tier icons mapping
const tierIcons: Record<TournamentTier, string> = {
  basic: "solar:shield-bold-duotone",
  competitive: "solar:crown-bold-duotone",
  premium: "fluent-color:premium-32",
};

interface TierSelectionCardsProps {
  onSelect: (tier: TournamentTier) => void;
  /** Compact mode for modal display */
  compact?: boolean;
  /** Show pricing information */
  showPricing?: boolean;
  /** Whether the buttons are disabled */
  disabled?: boolean;
  /** Which tiers to display (defaults to all) */
  tiers?: TournamentTier[];
}

export function TierSelectionCards({
  onSelect,
  compact = false,
  showPricing = true,
  disabled = false,
  tiers = ["basic", "competitive", "premium"],
}: TierSelectionCardsProps) {
  const t = useTranslations();

  return (
    <div
      className={cn(
        "grid grid-cols-1 w-full",
        tiers.length === 2 && "lg:grid-cols-2",
        tiers.length >= 3 && "lg:grid-cols-3",
        compact ? "gap-4 md:gap-6" : "gap-6 md:gap-8 max-w-5xl",
      )}
    >
      {(tiers as readonly TournamentTier[]).map((tier) => {
        const isPopular = tier === "competitive";
        const isPremium = tier === "premium";

        return (
          <div
            key={tier}
            className={cn(
              "relative group  w-full",
              isPopular &&
                (compact ? "lg:-mt-2 lg:mb-2 z-10" : "lg:-mt-4 lg:mb-4 z-10"),
            )}
          >
            {/* Popular Badge */}
            {isPopular && (
              <div
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 z-20",
                  compact ? "-top-3" : "-top-4",
                )}
              >
                <div className="relative">
                  <div
                    className={cn(
                      "bg-gradient-to-r from-[var(--gold-dark)] via-[var(--gold)] to-[var(--gold-dark)] text-[var(--bg-deep)] font-cinzel text-xs font-bold uppercase tracking-wider rounded-sm shadow-lg",
                      compact ? "px-4 py-1" : "px-6 py-1.5",
                    )}
                  >
                    {t("startTournament.mostPopular")}
                  </div>
                  {/* Banner tails */}
                  <div
                    className={cn(
                      "absolute left-0 w-0 h-0 border-l-transparent border-t-[var(--gold-dark)]",
                      compact
                        ? "-bottom-1.5 border-l-[8px] border-t-[6px]"
                        : "-bottom-2 border-l-[12px] border-t-[8px]",
                    )}
                  />
                  <div
                    className={cn(
                      "absolute right-0 w-0 h-0 border-r-transparent border-t-[var(--gold-dark)]",
                      compact
                        ? "-bottom-1.5 border-r-[8px] border-t-[6px]"
                        : "-bottom-2 border-r-[12px] border-t-[8px]",
                    )}
                  />
                </div>
              </div>
            )}

            {/* Card Container */}
            <div
              className={cn(
                "relative h-full rounded-lg overflow-hidden transition-[border-color,box-shadow] duration-300  w-full",
                "bg-[var(--bg-card)] border",
                isPopular
                  ? compact
                    ? "border-[var(--gold)] shadow-[0_0_20px_rgba(255,215,0,0.15)]"
                    : "border-[var(--gold)] shadow-[0_0_30px_rgba(255,215,0,0.2)]"
                  : "border-[var(--border-card)] hover:border-[var(--gold)]/40",
                compact
                  ? "hover:shadow-[0_0_30px_rgba(255,215,0,0.1)] cursor-pointer"
                  : "hover:shadow-[0_0_40px_rgba(255,215,0,0.15)]",
              )}
              onClick={compact ? () => onSelect(tier) : undefined}
              onKeyDown={
                compact
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(tier);
                      }
                    }
                  : undefined
              }
              role={compact ? "button" : undefined}
              tabIndex={compact ? 0 : undefined}
              aria-label={
                compact ? t(`startTournament.tiers.${tier}.name`) : undefined
              }
            >
              {/* Decorative Corner Accents */}
              <div
                className={cn(
                  "absolute top-0 left-0 border-t-2 border-l-2 border-[var(--gold)]/50 rounded-tl-lg",
                  compact ? "w-4 h-4" : "w-6 h-6",
                )}
              />
              <div
                className={cn(
                  "absolute top-0 right-0 border-t-2 border-r-2 border-[var(--gold)]/50 rounded-tr-lg",
                  compact ? "w-4 h-4" : "w-6 h-6",
                )}
              />
              <div
                className={cn(
                  "absolute bottom-0 left-0 border-b-2 border-l-2 border-[var(--gold)]/50 rounded-bl-lg",
                  compact ? "w-4 h-4" : "w-6 h-6",
                )}
              />
              <div
                className={cn(
                  "absolute bottom-0 right-0 border-b-2 border-r-2 border-[var(--gold)]/50 rounded-br-lg",
                  compact ? "w-4 h-4" : "w-6 h-6",
                )}
              />

              {/* Card Content */}
              <div
                className={cn(
                  "flex flex-col h-full",
                  compact ? "p-4 md:p-6" : "p-6 md:p-8",
                )}
              >
                {/* Tier Header */}
                <div className={cn("text-center", compact ? "mb-4" : "mb-6")}>
                  {/* Tier Icon */}
                  <div
                    className={cn(
                      "inline-flex items-center justify-center rounded-full",
                      compact
                        ? "w-12 h-12 mb-3"
                        : "w-14 h-14 md:w-16 md:h-16 mb-4",
                      isPopular
                        ? "bg-gradient-to-br from-[var(--gold)] to-[var(--gold-dark)]"
                        : isPremium
                          ? "bg-gradient-to-br from-purple-500/30 to-purple-900/30 border border-purple-500/40"
                          : "bg-gradient-to-br from-slate-600/30 to-slate-800/30 border border-slate-400/40",
                    )}
                  >
                    <Icon
                      icon={tierIcons[tier]}
                      className={cn(
                        compact ? "w-6 h-6" : "w-7 h-7 md:w-8 md:h-8",
                        isPopular
                          ? "text-[var(--bg-deep)]"
                          : isPremium
                            ? "text-purple-400"
                            : "text-slate-300",
                      )}
                    />
                  </div>

                  {/* Tier Name */}
                  <h2
                    className={cn(
                      "font-cinzel font-bold tracking-wide",
                      compact ? "text-lg mb-1" : "text-xl md:text-2xl mb-2",
                      isPopular
                        ? "text-[var(--gold)]"
                        : isPremium
                          ? "text-purple-300"
                          : "text-slate-200",
                    )}
                  >
                    {t(`startTournament.tiers.${tier}.name`)}
                  </h2>

                  {/* Description */}
                  <p
                    className={cn(
                      "text-primary font-spectral",
                      compact ? "text-xs" : "text-sm",
                    )}
                  >
                    {t(`startTournament.tiers.${tier}.description`)}
                  </p>

                  {/* Additional Info */}
                  <p
                    className={cn(
                      "text-primary/60 text-center font-spectral italic",
                      compact ? "text-xs mt-2" : "text-xs my-4",
                    )}
                  >
                    {t(`startTournament.tiers.${tier}.additionalInfo`)}
                  </p>
                </div>

                {/* Decorative Divider */}
                <div className={cn("relative h-px", compact ? "mb-4" : "mb-6")}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[var(--gold)]/40 to-transparent" />
                  <div
                    className={cn(
                      "absolute left-1/2 -translate-x-1/2 rotate-45 bg-[var(--bg-card)] border border-[var(--gold)]/40",
                      compact ? "-top-1 w-2 h-2" : "-top-1.5 w-3 h-3",
                    )}
                  />
                </div>

                {/* Pricing - Only show if showPricing is true */}
                {showPricing && (
                  <div className={cn("text-center", compact ? "mb-4" : "mb-6")}>
                    <div className="flex items-baseline justify-center gap-1">
                      <span
                        className={cn(
                          "font-cinzel font-bold",
                          compact ? "text-3xl" : "text-4xl md:text-5xl",
                          isPopular
                            ? "text-[var(--gold)]"
                            : isPremium
                              ? "text-purple-300"
                              : "text-slate-200",
                        )}
                      >
                        {t(`startTournament.tiers.${tier}.basePrice`)}
                      </span>
                      <span className="text-muted text-sm">
                        {t("startTournament.perTournament")}
                      </span>
                    </div>
                    <p className="text-primary text-sm mt-1">
                      + {t(`startTournament.tiers.${tier}.matchPrice`)}{" "}
                      {t("startTournament.perMatch")}
                    </p>
                  </div>
                )}

                {/* Features List */}
                <ul
                  className={cn(
                    "flex flex-col flex-grow",
                    compact ? "gap-2 mb-4" : "gap-3 mb-6",
                  )}
                >
                  {(t.raw(`startTournament.tiers.${tier}.features`) as string[])
                    .slice(0, compact ? 4 : undefined)
                    .map((feature, idx) => (
                      <li
                        key={idx}
                        className={cn(
                          "flex items-start",
                          compact ? "gap-2" : "gap-3",
                        )}
                      >
                        <div
                          className={cn(
                            "shrink-0 mt-0.5 rounded-full flex items-center justify-center",
                            compact ? "w-4 h-4" : "w-5 h-5",
                            isPopular
                              ? "bg-[var(--gold)]/20"
                              : isPremium
                                ? "bg-purple-500/20"
                                : "bg-slate-400/20",
                          )}
                        >
                          <Icon
                            className={cn(
                              compact ? "w-2.5 h-2.5" : "w-3 h-3",
                              isPopular
                                ? "text-[var(--gold)]"
                                : isPremium
                                  ? "text-purple-400"
                                  : "text-slate-300",
                            )}
                            icon="solar:check-read-bold"
                          />
                        </div>
                        <span
                          className={cn(
                            "text-secondary font-spectral",
                            compact ? "text-xs" : "text-sm",
                          )}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                </ul>

                {/* Striimy Note - Only for Premium and non-compact mode */}
                {isPremium && !compact && (
                  <p className="text-muted text-xs text-center mb-4 font-spectral">
                    {t("startTournament.striimyNote")}{" "}
                    <a
                      href="https://striimy.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-[var(--gold-light)]"
                    >
                      striimy.com
                    </a>
                  </p>
                )}

                {/* CTA Button */}
                <Button
                  fullWidth
                  isDisabled={disabled}
                  onPress={() => onSelect(tier)}
                  title={
                    disabled
                      ? t("startTournament.betaDisabledMessage")
                      : undefined
                  }
                  className={cn(
                    "font-cinzel font-semibold tracking-wide transition-shadow duration-300",
                    isPopular
                      ? "bg-gradient-to-r from-[var(--gold-dark)] via-[var(--gold)] to-[var(--gold-dark)] text-[var(--bg-deep)] hover:shadow-[0_0_20px_rgba(255,215,0,0.4)]"
                      : isPremium
                        ? "bg-gradient-to-r from-purple-600 to-purple-800 text-white hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] border-0"
                        : "bg-transparent border-2 border-slate-400/50 text-slate-200 hover:bg-slate-400/10 hover:border-slate-300",
                  )}
                  size={compact ? "md" : "lg"}
                >
                  {t("startTournament.getStarted")}
                </Button>
              </div>
              {disabled && <p>{t("startTournament.betaDisabledMessage")}</p>}
              {/* Subtle background glow for popular tier */}
              {isPopular && (
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--gold)]/5 via-transparent to-[var(--gold)]/5 pointer-events-none" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
